import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"
import { ArrowLeft, Save } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useUserName, useTienePermiso } from '../App';
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { RechazoModal } from "./RechazoModal";

interface NMXA50776330FormData {
  // Información general
  fecha: string
  hora: string
  grados: string // <-- agregado
  hr: string     // <-- agregado
  ciclos: string
  lavado: string
  secado: string
  tipo_de_lavadora: string
  tipo_lavado_ora: string
  tipo_de_secado_ora: string
  tipo_de_carga: string
  masa_de_carga: string
  detergente: string
  numero_solicitud: string
  id_termohigrometro: string
  DIM_ESPECIM?: string;
  
  // Muestra 1 - Urdimbre/Columnas
  m1_urd_inicial_1: string
  m1_urd_inicial_2: string
  m1_urd_inicial_3: string
  m1_urd_inicial_prom: string
  m1_urd_final_1: string
  m1_urd_final_2: string
  m1_urd_final_3: string
  m1_urd_final_prom: string
  m1_urd_cambio: string
  
  // Muestra 1 - Trama/Mallas
  m1_tra_inicial_1: string
  m1_tra_inicial_2: string
  m1_tra_inicial_3: string
  m1_tra_inicial_prom: string
  m1_tra_final_1: string
  m1_tra_final_2: string
  m1_tra_final_3: string
  m1_tra_final_prom: string
  m1_tra_cambio: string
  
  // Muestra 2 - Urdimbre/Columnas
  m2_urd_inicial_1: string
  m2_urd_inicial_2: string
  m2_urd_inicial_3: string
  m2_urd_inicial_prom: string
  m2_urd_final_1: string
  m2_urd_final_2: string
  m2_urd_final_3: string
  m2_urd_final_prom: string
  m2_urd_cambio: string
  
  // Muestra 2 - Trama/Mallas
  m2_tra_inicial_1: string
  m2_tra_inicial_2: string
  m2_tra_inicial_3: string
  m2_tra_inicial_prom: string
  m2_tra_final_1: string
  m2_tra_final_2: string
  m2_tra_final_3: string
  m2_tra_final_prom: string
  m2_tra_cambio: string
  
  // Promedios
  promedio_urdimbre: string
  promedio_trama: string
  
  // Redondeo
  redondeo_urdimbre: string
  redondeo_trama: string

  // Personal
  realizo: string
  superviso: string

  // Observaciones
  observaciones: string
  mostrarObservaciones: boolean
}

interface NMXA50776330FormProps {
  mode: "create" | "view" | "edit"
  initialData?: Partial<NMXA50776330FormData>
  onSave?: (data: NMXA50776330FormData) => void
  onCancel?: () => void
  rol?: string
  pedidoId?: string
  pedidoRowId?: string
  pruebaNombre?: string
  realizoUser?: string
  onRefreshBackend?: () => void // <-- Agregado
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

// Utility to truncate to 1 decimal without rounding
function truncate1Decimal(num: number): string {
  const truncated = Math.trunc(num * 10) / 10;
  return truncated.toFixed(1);
}

// Utility to truncate to 2 decimals without rounding
function truncate2Decimals(num: number): string {
  const truncated = Math.trunc(num * 100) / 100;
  return truncated.toFixed(2);
}

// Calculate averages for each set of 3 values
function calculateAverage(values: (string | number)[]): string {
  const nums = values.map(v => parseFloat(String(v))).filter(v => !isNaN(v));
  if (nums.length === 0) return '';
  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  return truncate1Decimal(avg);
}

// Algoritmo de redondeo al múltiplo de 0.5 más cercano, conservando el signo
function customRound(val: string): string {
  const num = parseFloat(val);
  if (isNaN(num)) return '';
  // Redondear al múltiplo de 0.5 más cercano, alejándose de cero en los empates (.75 sube a 1.0)
  const sign = num < 0 ? -1 : 1;
  const rounded = (Math.round(Math.abs(num) * 2) / 2) * sign;
  return rounded.toString();
}

// Calcula el % de cambio dimensional truncado a 2 decimales sin redondear
function cambioDimensional(finalProm: string, inicialProm: string): string {
  const inicial = parseFloat(inicialProm);
  const final = parseFloat(finalProm);
  if (isNaN(inicial) || inicial === 0 || isNaN(final)) return '';
  const val = ((final - inicial) / inicial) * 100;
  return truncate2Decimals(val);
}

export function NMXA50776330Form({
  mode,
  initialData,
  onSave,
  onCancel,
  rol,
  pedidoId = '',
  pedidoRowId,
  pruebaNombre = '',
  realizoUser,
  onRefreshBackend, // <-- Agregado
  onAutosave
}: NMXA50776330FormProps) {
  const globalRol = useUserRol();
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = rol || globalRol;
  console.log('DEBUG rol prop in NMXA50776330Form:', effectiveRol);
  console.log('DEBUG all props in NMXA50776330Form:', { mode, initialData, onSave, onCancel, rol, pedidoId, pruebaNombre });

  const [formData, setFormData] = useState<NMXA50776330FormData>(() => ({
    fecha: "",
    hora: "",
    grados: "", // <-- inicializado
    hr: "",     // <-- inicializado
    ciclos: "",
    lavado: "",
    secado: "",
    tipo_de_lavadora: initialData?.tipo_de_lavadora || "tipo B",
    tipo_lavado_ora: "",
    tipo_de_secado_ora: initialData?.tipo_de_secado_ora || "tipo A3",
    tipo_de_carga: "",
    masa_de_carga: "",
    detergente: "",
    numero_solicitud: initialData?.numero_solicitud || '',
    id_termohigrometro: initialData?.id_termohigrometro || '',
    DIM_ESPECIM: (initialData as any)?.DIM_ESPECIM || (initialData as any)?.dim_especim || '',
    m1_urd_inicial_1: "",
    m1_urd_inicial_2: "",
    m1_urd_inicial_3: "",
    m1_urd_inicial_prom: "",
    m1_urd_final_1: "",
    m1_urd_final_2: "",
    m1_urd_final_3: "",
    m1_urd_final_prom: "",
    m1_urd_cambio: "",
    m1_tra_inicial_1: "",
    m1_tra_inicial_2: "",
    m1_tra_inicial_3: "",
    m1_tra_inicial_prom: "",
    m1_tra_final_1: "",
    m1_tra_final_2: "",
    m1_tra_final_3: "",
    m1_tra_final_prom: "",
    m1_tra_cambio: "",
    m2_urd_inicial_1: "",
    m2_urd_inicial_2: "",
    m2_urd_inicial_3: "",
    m2_urd_inicial_prom: "",
    m2_urd_final_1: "",
    m2_urd_final_2: "",
    m2_urd_final_3: "",
    m2_urd_final_prom: "",
    m2_urd_cambio: "",
    m2_tra_inicial_1: "",
    m2_tra_inicial_2: "",
    m2_tra_inicial_3: "",
    m2_tra_inicial_prom: "",
    m2_tra_final_1: "",
    m2_tra_final_2: "",
    m2_tra_final_3: "",
    m2_tra_final_prom: "",
    m2_tra_cambio: "",
    promedio_urdimbre: "",
    promedio_trama: "",
    redondeo_urdimbre: "",
    redondeo_trama: "",
    realizo: "",
    superviso: "",
    observaciones: "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  }));

  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoReason, setRechazoReason] = useState("");
  const [rechazoLoading, setRechazoLoading] = useState(false);

  // --- ADMIN ACTIONS robustas igual que AnalisisQuimicoForm ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode);

  useEffect(() => {
    if (mode === 'create' && realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [mode, realizoUser])

  const handleInputChange = (field: keyof NMXA50776330FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const formPayload = () => formData

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    if (!onSave) {
      alert('No se recibió la función onSave.');
      console.error('onSave no está definido en NMXA50776330Form');
      return;
    }
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      onSave(formPayload())
    } finally {
      setGuardando(false)
    }
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-5077-INNTEX-2015 / NMX-A-6330-INNTEX-2015 CAMBIOS DIMENSIONALES',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleEditar = () => {
    setEditMode(true);
  };

  const handleGuardarEdicion = async () => {
    const supervisorName = userName || formData.superviso || '';
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-5077/6330? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      superviso: supervisorName,
      ROW_ID: rowId,
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  // --- ACCIÓN ADMIN: Aceptar/Rechazar ---
  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const supervisorName = userName || formData.superviso || '';
    if (accion === 'rechazado' && !razonRechazo) {
      setRechazoModalOpen(true);
      return;
    }
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-5077/6330?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-5077/6330?';
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      superviso: supervisorName,
      ROW_ID: rowId,
      ACEPTAR: accion,
      ACEPTADO: accion,
      ...(accion === 'rechazado' ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? razonRechazo : undefined, supervisorName, nomId);
      setFormData(prev => ({ ...prev, superviso: supervisorName }));
      setEditMode(false);
      setRechazoModalOpen(false);
      setRechazoReason("");
      setRechazoLoading(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      setRechazoLoading(false);
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "create":
        return "Crear NMX-A-5077/6330-INNTEX-2015"
      case "edit":
        return "Editar NMX-A-5077/6330-INNTEX-2015"
      case "view":
        return "Ver NMX-A-5077/6330-INNTEX-2015"
      default:
        return "NMX-A-5077/6330-INNTEX-2015"
    }
  }

  // Auto-calculate averages and % cambio dimensional when values change
  useEffect(() => {
    const m1_urd_inicial_prom = calculateAverage([formData.m1_urd_inicial_1, formData.m1_urd_inicial_2, formData.m1_urd_inicial_3]);
    const m1_urd_final_prom = calculateAverage([formData.m1_urd_final_1, formData.m1_urd_final_2, formData.m1_urd_final_3]);
    const m1_tra_inicial_prom = calculateAverage([formData.m1_tra_inicial_1, formData.m1_tra_inicial_2, formData.m1_tra_inicial_3]);
    const m1_tra_final_prom = calculateAverage([formData.m1_tra_final_1, formData.m1_tra_final_2, formData.m1_tra_final_3]);
    const m2_urd_inicial_prom = calculateAverage([formData.m2_urd_inicial_1, formData.m2_urd_inicial_2, formData.m2_urd_inicial_3]);
    const m2_urd_final_prom = calculateAverage([formData.m2_urd_final_1, formData.m2_urd_final_2, formData.m2_urd_final_3]);
    const m2_tra_inicial_prom = calculateAverage([formData.m2_tra_inicial_1, formData.m2_tra_inicial_2, formData.m2_tra_inicial_3]);
    const m2_tra_final_prom = calculateAverage([formData.m2_tra_final_1, formData.m2_tra_final_2, formData.m2_tra_final_3]);

    const promedio_urdimbre = (cambioDimensional(m1_urd_final_prom, m1_urd_inicial_prom) && cambioDimensional(m2_urd_final_prom, m2_urd_inicial_prom))
      ? truncate2Decimals((parseFloat(cambioDimensional(m1_urd_final_prom, m1_urd_inicial_prom)) + parseFloat(cambioDimensional(m2_urd_final_prom, m2_urd_inicial_prom))) / 2)
      : '';
    const promedio_trama = (cambioDimensional(m1_tra_final_prom, m1_tra_inicial_prom) && cambioDimensional(m2_tra_final_prom, m2_tra_inicial_prom))
      ? truncate2Decimals((parseFloat(cambioDimensional(m1_tra_final_prom, m1_tra_inicial_prom)) + parseFloat(cambioDimensional(m2_tra_final_prom, m2_tra_inicial_prom))) / 2)
      : '';

    setFormData(prev => ({
      ...prev,
      m1_urd_inicial_prom,
      m1_urd_final_prom,
      m1_tra_inicial_prom,
      m1_tra_final_prom,
      m2_urd_inicial_prom,
      m2_urd_final_prom,
      m2_tra_inicial_prom,
      m2_tra_final_prom,
      m1_urd_cambio: cambioDimensional(m1_urd_final_prom, m1_urd_inicial_prom),
      m1_tra_cambio: cambioDimensional(m1_tra_final_prom, m1_tra_inicial_prom),
      m2_urd_cambio: cambioDimensional(m2_urd_final_prom, m2_urd_inicial_prom),
      m2_tra_cambio: cambioDimensional(m2_tra_final_prom, m2_tra_inicial_prom),
      promedio_urdimbre,
      promedio_trama,
      redondeo_urdimbre: promedio_urdimbre ? customRound(promedio_urdimbre) : '',
      redondeo_trama: promedio_trama ? customRound(promedio_trama) : '',
    }));
  }, [
    formData.m1_urd_inicial_1, formData.m1_urd_inicial_2, formData.m1_urd_inicial_3,
    formData.m1_urd_final_1, formData.m1_urd_final_2, formData.m1_urd_final_3,
    formData.m1_tra_inicial_1, formData.m1_tra_inicial_2, formData.m1_tra_inicial_3,
    formData.m1_tra_final_1, formData.m1_tra_final_2, formData.m1_tra_final_3,
    formData.m2_urd_inicial_1, formData.m2_urd_inicial_2, formData.m2_urd_inicial_3,
    formData.m2_urd_final_1, formData.m2_urd_final_2, formData.m2_urd_final_3,
    formData.m2_tra_inicial_1, formData.m2_tra_inicial_2, formData.m2_tra_inicial_3,
    formData.m2_tra_final_1, formData.m2_tra_final_2, formData.m2_tra_final_3
  ]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackFlush}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>{getTitle()}</h1>
        </div>
        {/* Mostrar Guardar solo en modo create */}
        {mode === 'create' && !isReadOnly && (
          <Button onClick={handleSave} className="gap-2" data-testid="guardar-btn" disabled={guardando}>
            <Save className="h-4 w-4" />
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        )}
      </div>

      {/* Botones admin */}
      {puedeRevisar && mode === 'view' && !editMode && !isAceptado && (
        <div className="flex gap-2 mt-4">
          <Button onClick={() => handleAccion('aceptado')} variant="default">Aceptar</Button>
          <Button onClick={() => setRechazoModalOpen(true)} variant="destructive">Rechazar</Button>
          <Button onClick={handleEditar} variant="outline">Editar</Button>
        </div>
      )}
      {puedeRevisar && (mode === 'edit' || editMode) && !isAceptado && (
        <div className="flex gap-2 mt-4">
          <Button onClick={handleGuardarEdicion} variant="default">Guardar Edición</Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col items-start mb-4">
            <div>
              <h1 className="font-bold text-2xl leading-tight">NMX-A-5077-INNTEX-2015 / NMX-A-6330-INNTEX-2015</h1>
              <div className="text-lg font-medium">DETERMINACIÓN DEL CAMBIO DIMENSIONAL EN EL LAVADO Y EL SECADO</div>
            </div>
            <div className="mt-2 text-base font-semibold text-primary">Método: MET-7.2-18</div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Campos adicionales: Número de solicitud e ID de termohigrómetro */}
          <div className="flex flex-wrap gap-4 mb-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Número de solicitud</label>
              <Input
                type="text"
                value={formData.numero_solicitud}
                onChange={e => handleInputChange('numero_solicitud', e.target.value)}
                disabled={isReadOnly}
                className="border-0 bg-transparent text-xs h-6"
                placeholder="Ejemplo: 12345"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">ID de termohigrómetro</label>
              <select
                value={formData.id_termohigrometro}
                onChange={e => handleInputChange('id_termohigrometro', e.target.value)}
                disabled={isReadOnly}
                className="border-0 bg-transparent text-xs h-6"
              >
                <option value="">Selecciona...</option>
                <option value="E-59">E-59</option>
                <option value="E-57">E-57</option>
                <option value="E-56">E-56</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Dimensión de los especimenes</label>
              <Input
                type="text"
                value={formData.DIM_ESPECIM || ""}
                onChange={e => handleInputChange('DIM_ESPECIM', e.target.value)}
                disabled={isReadOnly}
                className="border-0 bg-transparent text-xs h-6"
                placeholder="Ejemplo: 10x5 cm"
              />
            </div>
          </div>

          {/* Tabla de información general */}
          <div className="overflow-x-auto w-full">
            <div className="inline-block w-full min-w-full">
              <table className="border-collapse border border-border text-xs table-auto w-full">
                <tbody>
                  {/* Primera fila */}
                  <tr>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">FECHA:</td>
                    <td className="border border-border p-1 w-32">
                      <Input
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6"
                      />
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">HORA:</td>
                    <td className="border border-border p-1 w-24">
                      <Input
                        type="time"
                        value={formData.hora}
                        onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6"
                      />
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">°C:</td>
                    <td className="border border-border p-1 w-16">
                      <Input
                        type="text"
                        value={formData.grados}
                        onChange={(e) => handleInputChange('grados', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6"
                      />
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">% HR:</td>
                    <td className="border border-border p-1 w-16">
                      <Input
                        type="text"
                        value={formData.hr}
                        onChange={(e) => handleInputChange('hr', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6"
                      />
                    </td>
                  </tr>

                  {/* Segunda fila */}
                  <tr>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">CICLOS:</td>
                    <td className="border border-border p-1">
                      <select
                        value={formData.ciclos}
                        onChange={e => handleInputChange('ciclos', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6 w-full"
                      >
                        <option value="">Selecciona</option>
                        {[1,2,3,4,5].map(n => (
                          <option key={n} value={String(n)}>{n}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">LAVADO:</td>
                    <td className="border border-border p-1">
                      <select
                        value={formData.lavado}
                        onChange={e => handleInputChange('lavado', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6 w-full"
                      >
                        <option value="">Selecciona</option>
                        {["1B","2B","3B","4B","5B","6B","7B","8B","9B","10B","11B"].map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">SECADO:</td>
                    <td className="border border-border p-1">
                      <select
                        value={formData.secado}
                        onChange={e => handleInputChange('secado', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6 w-full"
                      >
                        <option value="">Selecciona</option>
                        <option value="Procedimiento A – Secado en línea">Procedimiento A – Secado en línea</option>
                        <option value="Procedimiento B – Secado por escurrimiento">Procedimiento B – Secado por escurrimiento</option>
                        <option value="Procedimiento C – Secado en rejilla">Procedimiento C – Secado en rejilla</option>
                        <option value="Procedimiento D – Secado escurrimiento horizontal">Procedimiento D – Secado escurrimiento horizontal</option>
                        <option value="Procedimiento F – Secado en tómbola">Procedimiento F – Secado en tómbola</option>
                      </select>
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">TIPO DE LAVADORA:</td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        value={formData.tipo_de_lavadora}
                        onChange={e => handleInputChange('tipo_de_lavadora', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6"
                        placeholder="tipo B..."
                      />
                    </td>
                  </tr>

                  {/* Tercera fila */}
                  <tr>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">TIPO DE CARGA:</td>
                    <td className="border border-border p-1">
                      <select
                        value={formData.tipo_de_carga}
                        onChange={e => handleInputChange('tipo_de_carga', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6 w-full"
                      >
                        <option value="">Selecciona</option>
                        <option value="TIPO I">TIPO I</option>
                        <option value="TIPO II">TIPO II</option>
                        <option value="TIPO III">TIPO III</option>
                      </select>
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">MASA DE CARGA:</td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">2±0.1 kg</td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap">TIPO SECADORA:</td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        value={formData.tipo_de_secado_ora}
                        onChange={(e) => handleInputChange('tipo_de_secado_ora', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-xs h-6"
                        placeholder="tipo A3..."
                      />
                    </td>
                    <td className="border border-border px-2 py-1 bg-muted font-medium whitespace-nowrap" colSpan={2}>DETERGENTE: WOB AATCC 1993</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Secciones de muestras */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* MUESTRA 1 */}
            <div className="border border-border">
              <div className="bg-muted p-2 text-center font-medium text-sm">MUESTRA 1</div>
              
              <div className="grid grid-cols-2">
                {/* Urdimbre/Columnas */}
                <div className="border-r border-border">
                  <div className="bg-muted p-1 text-center font-medium text-xs">Urdimbre/ Columnas</div>
                  <div className="grid grid-cols-2">
                    <div className="border-r border-border">
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición inicial</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m1_urd_inicial_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m1_urd_inicial_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m1_urd_inicial_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición final</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m1_urd_final_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m1_urd_final_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m1_urd_final_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border p-2">
                    <div className="text-xs font-medium mb-1">% de cambio dimensional:</div>
                    <Input
                      type="text"
                      value={formData.m1_urd_cambio}
                      onChange={(e) => handleInputChange('m1_urd_cambio', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-xs h-6"
                    />
                  </div>
                </div>

                {/* Trama/Mallas */}
                <div>
                  <div className="bg-muted p-1 text-center font-medium text-xs">Trama / Mallas</div>
                  <div className="grid grid-cols-2">
                    <div className="border-r border-border">
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición inicial</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m1_tra_inicial_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m1_tra_inicial_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m1_tra_inicial_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición final</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m1_tra_final_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m1_tra_final_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m1_tra_final_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border p-2">
                    <div className="text-xs font-medium mb-1">% de cambio dimensional:</div>
                    <Input
                      type="text"
                      value={formData.m1_tra_cambio}
                      onChange={(e) => handleInputChange('m1_tra_cambio', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-xs h-6"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* MUESTRA 2 */}
            <div className="border border-border">
              <div className="bg-muted p-2 text-center font-medium text-sm">MUESTRA 2</div>
              
              <div className="grid grid-cols-2">
                {/* Urdimbre/Columnas */}
                <div className="border-r border-border">
                  <div className="bg-muted p-1 text-center font-medium text-xs">Urdimbre/ Columnas</div>
                  <div className="grid grid-cols-2">
                    <div className="border-r border-border">
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición inicial</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m2_urd_inicial_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m2_urd_inicial_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m2_urd_inicial_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición final</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m2_urd_final_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m2_urd_final_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m2_urd_final_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border p-2">
                    <div className="text-xs font-medium mb-1">% de cambio dimensional:</div>
                    <Input
                      type="text"
                      value={formData.m2_urd_cambio}
                      onChange={(e) => handleInputChange('m2_urd_cambio', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-xs h-6"
                    />
                  </div>
                </div>

                {/* Trama/Mallas */}
                <div>
                  <div className="bg-muted p-1 text-center font-medium text-xs">Trama / Mallas</div>
                  <div className="grid grid-cols-2">
                    <div className="border-r border-border">
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición inicial</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m2_tra_inicial_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m2_tra_inicial_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m2_tra_inicial_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="bg-muted p-1 text-center text-xs font-medium">Medición final</div>
                      {[1, 2, 3].map((num) => (
                        <div key={num} className="border-t border-border flex">
                          <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">{num}</div>
                          <div className="flex-1 p-1">
                            <Input
                              type="text"
                              value={formData[`m2_tra_final_${num}` as keyof NMXA50776330FormData] as string}
                              onChange={(e) => handleInputChange(`m2_tra_final_${num}` as keyof NMXA50776330FormData, e.target.value)}
                              disabled={isReadOnly}
                              className="border-0 bg-transparent text-xs h-5"
                            />
                          </div>
                        </div>
                      ))}
                      <div className="border-t border-border flex">
                        <div className="w-7 bg-muted text-center text-xs py-0.5 border-r leading-none">PRO</div>
                        <div className="flex-1 p-1">
                          <Input
                            type="text"
                            value={formData.m2_tra_final_prom}
                            disabled={true}
                            className="border-0 bg-transparent text-xs h-5"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border p-2">
                    <div className="text-xs font-medium mb-1">% de cambio dimensional:</div>
                    <Input
                      type="text"
                      value={formData.m2_tra_cambio}
                      onChange={(e) => handleInputChange('m2_tra_cambio', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-xs h-6"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Secciones finales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Promedios */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">PROMEDIOS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Promedio Urdimbre</label>
                  <Input
                    type="text"
                    value={formData.promedio_urdimbre}
                    disabled={true}
                    className="text-xs h-8"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Promedio Trama</label>
                  <Input
                    type="text"
                    value={formData.promedio_trama}
                    disabled={true}
                    className="text-xs h-8"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Redondeo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">REDONDEO</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Redondeo Urdimbre</label>
                  <Input
                    type="text"
                    value={formData.redondeo_urdimbre}
                    disabled={true}
                    className="text-xs h-8"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Redondeo Trama</label>
                  <Input
                    type="text"
                    value={formData.redondeo_trama}
                    disabled={true}
                    className="text-xs h-8"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Realizó / Supervisó */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-center">REALIZÓ</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="text"
                  value={formData.realizo}
                  onChange={(e) => handleInputChange('realizo', e.target.value)}
                  disabled
                  placeholder="Nombre del técnico"
                  className="text-xs h-8"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-center">SUPERVISÓ</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="text"
                  value={formData.superviso}
                  onChange={(e) => handleInputChange('superviso', e.target.value)}
                  disabled
                  placeholder="Nombre del supervisor"
                  className="text-xs h-8"
                />
              </CardContent>
            </Card>
          </div>

          {/* Observaciones */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm">OBSERVACIONES</CardTitle>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Mostrar en Reporte Cliente
                <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                  onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
              </span>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.observaciones}
                onChange={(e) => handleInputChange('observaciones', e.target.value)}
                disabled={isReadOnly}
                className="min-h-[100px] text-xs"
                placeholder="Ingrese observaciones adicionales..."
              />
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Modal de rechazo */}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}